import { 
  getRequests, 
  createRequest,
  updateRequest, 
  deleteRequest, 
  updateRequestStatus,
  createOption,
  updateOption,
  deleteOption
} from '@/api/request.api' 

import { REQUEST_STATUS } from "@/helpers/constants/app.constants";
import { sortByNumber } from '@/helpers/services/app.services';

export default {
  namespaced: true,

  state: {
    requests: []
  },

  getters: {
    requestAsItems: (state, getters, rootState, rootGetters) => {
      return sortByNumber(state.requests.map(request => ({
        ...request,
        sector: rootGetters['general/sectors'].find(sector => sector.id === request.sectorId)?.description,
        requestDate: request.createdAt ? new Date(request.createdAt).toLocaleDateString('pt-BR') : '-'
      })), 'code');
    },

    pendingRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.PENDING.id);
    },
    
    inPurchaseRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.IN_PURCHASE.id);
    },
    
    waitingApprovalRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.WAITING_APPROVAL.id);
    },
    
    approvedRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.APPROVED.id);
    },
    
    deniedRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.DENIED.id);
    },
    
    returnedRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.RETURNED.id);
    },
    
    deliveredRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.DELIVERED.id);
    },
    
    completedRequests: (state, getters) => {
      return getters.requestAsItems.filter(request => request.status === REQUEST_STATUS.COMPLETED.id);
    },
    
    getRequestById: (state, getters) => (id) => {
      return getters.requestAsItems.find(request => request.id === id);
    },
    
    getRequestsBySectorId: (state, getters) => (sectorId) => {
      return getters.requestAsItems.filter(request => request.sectorId === sectorId);
    },
    
    getRequestsByUserId: (state, getters) => (userId) => {
      return getters.requestAsItems.filter(request => request.userId === userId);
    }
  },

  mutations: {
    setRequests(state, requests) {
      state.requests = requests;
    },
    
    addRequest(state, request) {
      state.requests.push(request);
    },
    
    updateRequest(state, updatedRequest) {
      const index = state.requests.findIndex(request => request.id === updatedRequest.id);
      if (index !== -1) {
        state.requests.splice(index, 1, updatedRequest);
      }
    },
    
    removeRequest(state, requestId) {
      state.requests = state.requests.filter(request => request.id !== requestId);
    }
  },

  actions: {
    async initRequests({ commit }) {
      const { success, requests, error } = await getRequests();

      if (success) {
        commit('setRequests', requests);
        return { success: true };
      }

      return { success: false, error };
    },
    
    async createRequest({ commit }, requestData) {
      const { success, request, error } = await createRequest({
        ...requestData,
        id: undefined
      });
      
      if (success) {
        commit('addRequest', { ...request, requestOptions: [] });
        return { success: true, request };
      }
      
      return { success: false, error };
    },
    
    async updateRequest({ commit }, requestData) {
      const { success, request, error } = await updateRequest({
        ...requestData,
        sector: undefined,
        updatedAt: undefined,
        createdAt: undefined,
        code: undefined,
        requestDate: undefined,
        userId: undefined,
        status: undefined,
        requestOptions: undefined
      });
      
      if (success) {
        commit('updateRequest', request);
        return { success: true, request };
      }
      
      return { success: false, error };
    },
    
    async deleteRequest({ commit }, requestId) {
      const { success, error } = await deleteRequest(requestId);
      
      if (success) {
        commit('removeRequest', requestId);
        return { success: true };
      }
      
      return { success: false, error };
    },
    
    async updateStatus({ commit }, { requestId, status }) {
      const { success, request, error } = await updateRequestStatus(requestId, status);
      
      if (success) {
        commit('updateRequest', request);
        return { success: true, request };
      }
      
      return { success: false, error };
    },

    async createOption({ state, commit }, optionData) {
      const { success, option, error } = await createOption({
        ...optionData,
        id: undefined
      });
      
      if (success) {
        const requests = Array.from(state.requests);  
        const requestIndex = requests.findIndex(request => request.id === optionData.requestId);

        if (requestIndex !== -1) {
          requests[requestIndex].requestOptions.push(option);
        }

        commit('setRequests', requests);
        return { success: true, option };
      }
      
      return { success: false, error };
      },

    async updateOption({ state, commit }, optionData) {
      const { success, option, error } = await updateOption(optionData);
      
      if (success) {
        const requests = Array.from(state.requests);
        const requestIndex = requests.findIndex(request => request.id === option.requestId);
        
        if (requestIndex !== -1) {
          const requestOptionIndex = requests[requestIndex].requestOptions.findIndex(requestOption => requestOption.id === option.id);  

          if (requestOptionIndex !== -1) {
            requests[requestIndex].requestOptions[requestOptionIndex] = option;
          }
        }

        commit('setRequests', requests);
        return { success: true, option };
      }

      return { success: false, error };
    },

    async deleteOption({ state, commit }, optionId) {
      const { success, error } = await deleteOption(optionId);
      
      if (success) {
        const requests = Array.from(state.requests);
        const requestIndex = requests.findIndex(request => request.requestOptions.some(option => option.id === optionId));
        
        if (requestIndex !== -1) {
          requests[requestIndex].requestOptions = requests[requestIndex].requestOptions.filter(option => option.id !== optionId);
        }

        commit('setRequests', requests);
        return { success: true };
      }

      return { success: false, error };
    },

    async approveOption({ state, dispatch }, { requestId, optionId }) {
      const request = Array.from(state.requests).find(request => request.id === requestId);
      const option = request.requestOptions.find(option => option.id === optionId);

      if (!option || !request) {
        return { success: false, error: 'Não foi possivel aprovar a opção' };
      }

      for (const option of request.requestOptions) {
        if (option.id !== optionId) {
          await dispatch('deleteOption', option.id);
        }
      }

      const { success, error } = await dispatch('updateStatus', { 
        requestId, 
        status: REQUEST_STATUS.APPROVED.id, 
      });

      return { success, error };
    }
  }
} 